import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Heart, Loader2, TrendingUp, Shield, Brain, Scale, Layers, PieChart, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine,
} from "recharts";
import { api } from "@/lib/api";

const DIMS_META = {
  risk:          { label: "Risk",          Icon: Shield,   color: "#ef4444" },
  psychology:    { label: "Psychology",    Icon: Brain,    color: "#a855f7" },
  exposure:      { label: "Exposure",      Icon: Scale,    color: "#3b82f6" },
  correlation:   { label: "Correlation",   Icon: Layers,   color: "#f59e0b" },
  margin_health: { label: "Margin Health", Icon: PieChart, color: "#10b981" },
  drawdown:      { label: "Drawdown",      Icon: TrendingUp, color: "#ec4899" },
};

const scoreColor = (v) =>
  v >= 85 ? "#10b981" : v >= 70 ? "#3b82f6" : v >= 50 ? "#f59e0b" : "#ef4444";

const BigGauge = ({ score }) => {
  const s = Math.max(0, Math.min(100, score || 0));
  const color = scoreColor(s);
  const r = 100, c = 2 * Math.PI * r;
  const off = c * (1 - s / 100);
  return (
    <div className="relative w-64 h-64" data-testid="health-big-gauge">
      <svg viewBox="0 0 240 240" className="w-64 h-64 -rotate-90">
        <circle cx="120" cy="120" r={r} stroke="#1f2937" strokeWidth="14" fill="none" />
        <circle cx="120" cy="120" r={r} stroke={color} strokeWidth="14" fill="none"
                strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[10px] font-mono tracking-widest text-[#6b7280]">HEALTH</div>
        <div className="font-heading font-black text-7xl leading-none" style={{ color }}>
          {s}
        </div>
        <div className="text-xs font-mono text-[#9ca3af] mt-1">/ 100</div>
      </div>
    </div>
  );
};

const DimCard = ({ dimKey, value }) => {
  const meta = DIMS_META[dimKey] || { label: dimKey, Icon: Activity, color: "#9ca3af" };
  const Icon = meta.Icon;
  const color = scoreColor(value);
  return (
    <div
      className="rounded-lg border border-[#1f2937] bg-[#111827] p-5 hover:border-[#3b82f6]/40 transition-colors"
      data-testid={`dim-${dimKey}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
          <div className="vx-label" style={{ color: meta.color }}>{meta.label}</div>
        </div>
        <div className="font-heading text-3xl font-black" style={{ color }}>{value}</div>
      </div>
      <div className="h-2 bg-[#1f2937] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${value}%`, background: color }}
        />
      </div>
    </div>
  );
};

export default function HealthScore() {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [sparkline, setSparkline] = useState([]);
  const [snapping, setSnapping] = useState(false);

  const load = useCallback(async () => {
    try {
      const [h, s] = await Promise.all([
        api.get("/health-score"),
        api.get("/health-score/sparkline?days=30"),
      ]);
      setPayload(h.data);
      setSparkline(s.data?.points || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load health score");
    } finally {
      setLoading(false);
    }
  }, []);

  const snapshot = async () => {
    setSnapping(true);
    try {
      await api.post("/health-score/snapshot");
      toast.success("Today's snapshot saved");
      await load();
    } catch (e) {
      toast.error("Snapshot failed");
    } finally {
      setSnapping(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]" data-testid="health-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#3b82f6]" />
      </div>
    );
  }

  const dims = payload?.dimensions || {};
  const composite = payload?.composite ?? 0;
  const chartData = (sparkline || []).map(p => ({
    day: p.day?.slice(5), // MM-DD
    score: p.composite,
  }));

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="health-score-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="vx-label mb-1 flex items-center gap-2">
            <Heart className="w-3 h-3" /> Trading Health Score
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tighter">
            Account Vitals
          </h1>
          <p className="text-sm text-[#9ca3af] mt-2 max-w-2xl leading-relaxed">
            A single number that tells you whether your account is healthy enough to trade.
            Six dimensions weighted by capital-preservation importance. If this drops,
            ATI trims your lots automatically.
          </p>
        </div>
        <button
          onClick={snapshot}
          disabled={snapping}
          className="text-[10px] font-mono uppercase tracking-widest text-[#9ca3af] hover:text-[#3b82f6] transition-colors disabled:opacity-40"
          data-testid="health-snapshot-btn"
        >
          {snapping ? "Saving…" : "↻ Snapshot Today"}
        </button>
      </div>

      {/* Gauge + narrative */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-6 flex flex-col items-center justify-center gap-4">
          <BigGauge score={composite} />
          <div className="text-center text-sm text-[#9ca3af] max-w-xs leading-relaxed">
            {payload?.narrative}
          </div>
        </div>

        {/* 30-day sparkline */}
        <div className="lg:col-span-2 rounded-lg border border-[#1f2937] bg-[#111827] p-5" data-testid="health-sparkline">
          <div className="flex items-center justify-between mb-3">
            <div className="vx-label">30-Day Trend</div>
            <div className="text-[10px] font-mono text-[#6b7280]">{chartData.length} snapshots</div>
          </div>
          {chartData.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-sm text-[#9ca3af]">
              No history yet — click &quot;Snapshot Today&quot; or wait for the nightly job.
            </div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="hg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor="#3b82f6" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" tick={{ fill: "#6b7280", fontSize: 10 }} stroke="#1f2937" />
                  <YAxis domain={[0, 100]} tick={{ fill: "#6b7280", fontSize: 10 }} stroke="#1f2937" />
                  <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1f2937", fontSize: 12 }} />
                  <ReferenceLine y={70} stroke="#10b981" strokeDasharray="3 3" strokeOpacity={0.4} />
                  <ReferenceLine y={40} stroke="#ef4444" strokeDasharray="3 3" strokeOpacity={0.4} />
                  <Area type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2} fill="url(#hg)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* 6-dimension breakdown */}
      <div>
        <div className="vx-label mb-3">Six-Dimension Breakdown</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(dims).map(([k, v]) => (
            <DimCard key={k} dimKey={k} value={v} />
          ))}
        </div>
      </div>

      {/* Raw metrics */}
      {payload?.raw && (
        <div className="rounded-lg border border-[#1f2937] bg-[#111827] p-5">
          <div className="vx-label mb-3">Raw Metrics</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280]">Drawdown</div>
              <div className="font-mono">{payload.raw.drawdown_pct?.toFixed(2)}% / cap {payload.raw.max_dd_cap}%</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280]">Daily P&L</div>
              <div className="font-mono">${payload.raw.daily_pnl?.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280]">Margin Used</div>
              <div className="font-mono">{payload.raw.margin_util_pct?.toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280]">Consec Losses</div>
              <div className="font-mono">{payload.raw.consecutive_losses}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280]">Recent Win Rate</div>
              <div className="font-mono">{(payload.raw.recent_win_rate * 100).toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280]">Open Positions</div>
              <div className="font-mono">{payload.raw.open_positions}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
